import { cn } from '@llura/ui';

import { useStrings } from '../../hooks/useStrings';
import { useHaptic } from '../../hooks/useHaptic';
import type { RitualIntensity, RitualPeriod } from '../../stores/ritual.store';

export const CADENCE_COUNT_OPTIONS = [1, 2, 3, 4, 5, 6, 7] as const;
export const CADENCE_PERIOD_OPTIONS: readonly RitualPeriod[] = ['day', 'week'];

// Preset chips seed a count+period pair; hand-picking either afterwards clears
// the chip. Shared with the intro ritual screen and the notifications settings.
export const INTENSITY_DEFAULTS: Record<
  RitualIntensity,
  { count: number; period: RitualPeriod }
> = {
  soft: { count: 1, period: 'week' },
  challenging: { count: 3, period: 'week' },
  push: { count: 2, period: 'day' },
};

export interface CadenceValue {
  count: number | null;
  period: RitualPeriod | null;
  intensity: RitualIntensity | null;
}

interface CadencePickerProps {
  value: CadenceValue;
  onChange: (next: CadenceValue) => void;
}

/**
 * The ritual cadence picker — intensity presets, a count row, and a period
 * row — extracted from the intro so the same interface drives both onboarding
 * and the Notifications settings screen (where the cadence sets how often the
 * reminder fires). Fully controlled: the parent owns `value` and decides when
 * to persist.
 */
export function CadencePicker({ value, onChange }: CadencePickerProps) {
  const { strings } = useStrings();
  const haptic = useHaptic();
  const intensityLabels = strings.intro.screen5.intensities;
  const periodLabels = strings.intro.screen5.periods;

  const pickIntensity = (next: RitualIntensity) => {
    haptic.tap();
    const defaults = INTENSITY_DEFAULTS[next];
    onChange({ count: defaults.count, period: defaults.period, intensity: next });
  };

  const pickCount = (next: number) => {
    haptic.tap();
    // Hand-picking the count breaks the preset — drop the chip, keep the period.
    onChange({ count: next, period: value.period, intensity: null });
  };

  const pickPeriod = (next: RitualPeriod) => {
    haptic.tap();
    onChange({ count: value.count, period: next, intensity: null });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-center gap-2">
        {(['soft', 'challenging', 'push'] as const).map((key) => (
          <PillButton
            key={key}
            active={value.intensity === key}
            onClick={() => pickIntensity(key)}
          >
            {intensityLabels[key]}
          </PillButton>
        ))}
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {CADENCE_COUNT_OPTIONS.map((n) => (
          <CountButton key={n} active={value.count === n} onClick={() => pickCount(n)}>
            {n}
            <span className="ml-0.5 text-xs text-muted-foreground">
              {strings.intro.screen5.timesGlyph}
            </span>
          </CountButton>
        ))}
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {CADENCE_PERIOD_OPTIONS.map((p) => (
          <PillButton key={p} active={value.period === p} onClick={() => pickPeriod(p)}>
            {periodLabels[p]}
          </PillButton>
        ))}
      </div>
    </div>
  );
}

interface PillButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function PillButton({ active, onClick, children }: PillButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-4 py-2 text-sm transition-all',
        active
          ? 'border-primary bg-primary/15 text-foreground shadow-[0_0_24px_hsl(var(--primary)/0.25)]'
          : 'border-border bg-card/60 text-muted-foreground hover:border-primary/40 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function CountButton({ active, onClick, children }: PillButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-11 min-w-11 items-center justify-center rounded-full border px-3 text-base transition-all',
        active
          ? 'border-primary bg-primary/15 text-foreground shadow-[0_0_24px_hsl(var(--primary)/0.25)]'
          : 'border-border bg-card/60 text-muted-foreground hover:border-primary/40 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
